/**
 * Portal session bridge — universal portal cookie → workspace JWT.
 *
 * Flow:
 *   1. Magic link clicked → portal cookie set
 *   2. POST /api/auth/bridge with cookie → workspace JWT returned
 *   3. JWT stored in sessionStorage, used for all subsequent API calls
 *   4. Same-origin, credentials: "include"
 *
 * Ported from spec — clean implementation, no fork sludge.
 */

const JWT_STORAGE_KEY = "rida_workspace_jwt";
const JWT_EXPIRY_KEY = "rida_workspace_jwt_expiry";

export interface BridgeResult {
  ok: boolean;
  jwt?: string;
  expires_at?: number;
  tenant_id?: string;
  role?: "client" | "principal";
  error?: string;
}

/**
 * Exchange the portal cookie for a workspace JWT via the auth bridge.
 * Requires same-origin context (cookie sent automatically with credentials: "include").
 */
export async function bridgeSession(): Promise<BridgeResult> {
  try {
    const res = await fetch("/api/auth/bridge", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include", // CRITICAL: send the portal cookie
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      return { ok: false, error: err.error || `HTTP ${res.status}` };
    }

    const data = await res.json();
    if (data.jwt) {
      storeJWT(data.jwt, data.expires_at);
    }
    return {
      ok: true,
      jwt: data.jwt,
      expires_at: data.expires_at,
      tenant_id: data.tenant_id,
      role: data.role || "client",
    };
  } catch (err: any) {
    return { ok: false, error: err.message || "Network error" };
  }
}

/**
 * Request a magic link to be sent to the given email.
 * In dev mode, the server returns a dev_token that can be used to sign in directly.
 */
export async function requestMagicLink(email: string): Promise<{ ok: boolean; error?: string; dev_token?: string }> {
  try {
    const res = await fetch("/api/auth/magic-link", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      return { ok: false, error: err.error || `HTTP ${res.status}` };
    }
    const data = await res.json();
    return { ok: true, dev_token: data.dev_token };
  } catch (err: any) {
    return { ok: false, error: err.message || "Network error" };
  }
}

/**
 * Verify a magic link token (from URL param or click).
 */
export async function verifyMagicLink(token: string): Promise<BridgeResult> {
  try {
    const res = await fetch("/api/auth/verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ token }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: res.statusText }));
      return { ok: false, error: err.error || `HTTP ${res.status}` };
    }
    const data = await res.json();
    if (data.jwt) {
      storeJWT(data.jwt, data.expires_at);
    }
    return {
      ok: true,
      jwt: data.jwt,
      expires_at: data.expires_at,
      tenant_id: data.tenant_id,
      role: data.role || "client",
    };
  } catch (err: any) {
    return { ok: false, error: err.message || "Network error" };
  }
}

export function storeJWT(jwt: string, expiresAt?: number): void {
  sessionStorage.setItem(JWT_STORAGE_KEY, jwt);
  if (expiresAt) {
    sessionStorage.setItem(JWT_EXPIRY_KEY, String(expiresAt));
  }
}

export function getJWT(): string | null {
  const jwt = sessionStorage.getItem(JWT_STORAGE_KEY);
  if (!jwt) return null;
  const expiry = sessionStorage.getItem(JWT_EXPIRY_KEY);
  if (expiry && Date.now() > Number(expiry) * 1000) {
    clearJWT();
    return null;
  }
  return jwt;
}

export function clearJWT(): void {
  sessionStorage.removeItem(JWT_STORAGE_KEY);
  sessionStorage.removeItem(JWT_EXPIRY_KEY);
}

export function isAuthed(): boolean {
  return !!getJWT();
}
